import { useState } from "react";
import { Button, Icon, Spinner, Tag, Text, Title } from "components/common";
import PageLayout from "components/common/pageLayout/pageLayout";
import DemoDataToggle from "components/custom/demoDataToggle";
import { UserAvatar, badgeColor } from "components/custom/userListRow";
import { useGetUserComparisonQuery } from "features/community/apis";
import { demoComparison } from "features/community/mockData/social";
import { useNavigate, useParams } from "react-router-dom";
import { getUserProfilePath } from "constants/app-paths";
import styles from "./Compare.module.less";

const number = (value) => Number(value || 0).toLocaleString();
const hasValue = (value) => value !== null && value !== undefined;

const personForAvatar = (person) => ({
  name: person?.name,
  username: person?.username,
  profile_url: person?.avatar,
});

const ProfileIdentity = ({ person, label, onOpen }) => (
  <article className={styles.identityCard}>
    <span className={styles.identityLabel}>{label}</span>
    <button type="button" className={styles.avatarButton} onClick={onOpen} aria-label={`Open ${person.name}'s profile`}>
      <UserAvatar user={personForAvatar(person)} size={64} />
    </button>
    <button type="button" className={styles.profileLink} onClick={onOpen}>
      <strong>{person.name}</strong>
      {person.username && <span>@{person.username}</span>}
    </button>
    <div className={styles.profileMeta}>
      {person.role && <Tag color={badgeColor(person.role)} type="light" pill>{person.role}</Tag>}
      {person.educationLevel && <span>{person.educationLevel}</span>}
    </div>
  </article>
);

const Difference = ({ current, other, suffix = "" }) => {
  if (!hasValue(current) || !hasValue(other) || Number(current) === Number(other)) return null;
  const gap = Math.abs(Number(current) - Number(other));
  const youLead = Number(current) > Number(other);
  return <span className={styles.difference}>{youLead ? "You" : "They"} +{number(gap)}{suffix}</span>;
};

const OverallMetric = ({ icon, label, current, other, suffix = "", currentPrivate = false, otherPrivate = false }) => {
  const max = Math.max(Number(current || 0), Number(other || 0));
  const currentWidth = hasValue(current) && max > 0 ? `${(Number(current) / max) * 100}%` : "0%";
  const otherWidth = hasValue(other) && max > 0 ? `${(Number(other) / max) * 100}%` : "0%";
  return (
    <article className={styles.metricCard}>
      <div className={styles.metricHeading}><Icon icon={icon} size={16} /><span>{label}</span><Difference current={current} other={other} suffix={suffix} /></div>
      <div className={styles.metricValues}>
        <div><span>You</span><strong>{hasValue(current) ? `${number(current)}${suffix}` : currentPrivate ? "Private" : "No data"}</strong></div>
        <span className={styles.vs}>vs</span>
        <div><span>They</span><strong>{hasValue(other) ? `${number(other)}${suffix}` : otherPrivate ? "Private" : "No data"}</strong></div>
      </div>
      <div className={styles.comparisonBars} aria-label={`${label} comparison`}>
        <div><span className={styles.barLabel}>You</span><div className={styles.barTrack}><i className={styles.youBar} style={{ width: currentWidth }} /></div></div>
        <div><span className={styles.barLabel}>They</span><div className={styles.barTrack}><i className={styles.otherBar} style={{ width: otherWidth }} /></div></div>
      </div>
    </article>
  );
};

const SpecialtyRow = ({ item, otherName, isOtherPrivate = false }) => {
  const you = item.currentUser;
  const other = item.comparisonUser;
  const max = Math.max(Number(you?.accuracy || 0), Number(other?.accuracy || 0));
  const width = (value) => (hasValue(value) && max > 0 ? `${(Number(value) / max) * 100}%` : "0%");
  return (
    <article className={styles.specialtyRow}>
      <div className={styles.specialtyName}><strong>{item.name}</strong><span>Accuracy</span></div>
      <div className={styles.specialtyValue}>
        <span>You</span><strong>{hasValue(you?.accuracy) ? `${you.accuracy}%` : "No data"}</strong>
        {hasValue(you?.questionsAnswered) && <small>{number(you.questionsAnswered)} questions</small>}
        <div className={styles.barTrack}><i className={styles.youBar} style={{ width: width(you?.accuracy) }} /></div>
      </div>
      <div className={styles.specialtyValue}>
        <span>{otherName}</span><strong>{hasValue(other?.accuracy) ? `${other.accuracy}%` : isOtherPrivate ? "Private" : "No data"}</strong>
        {hasValue(other?.questionsAnswered) && <small>{number(other.questionsAnswered)} questions</small>}
        <div className={styles.barTrack}><i className={styles.otherBar} style={{ width: width(other?.accuracy) }} /></div>
      </div>
    </article>
  );
};

const ComparisonSkeleton = () => <div className={styles.skeleton} aria-label="Loading comparison"><div /><div /><div /><div /></div>;

const Compare = () => {
  const { userCode } = useParams();
  const navigate = useNavigate();
  const canUseDemoData = process.env.NODE_ENV !== "production";
  const [useDemoData, setUseDemoData] = useState(false);
  const { data: liveData, isLoading, isFetching, isError, error, refetch } = useGetUserComparisonQuery(userCode, { skip: !userCode || useDemoData });
  const data = useDemoData ? demoComparison : liveData;

  const errorMessage = error?.data?.message || error?.data?.msg;
  if (isLoading && !useDemoData) return <PageLayout backIcon label={<Title level={3}>Compare</Title>}><main className={styles.page}><ComparisonSkeleton />{canUseDemoData && <DemoDataToggle value={useDemoData} onChange={setUseDemoData} label="Compare data" />}</main></PageLayout>;
  if (isError || !data) return (
    <PageLayout backIcon label={<Title level={3}>Compare</Title>}>
      <main className={styles.page}><section className={styles.stateCard}><Icon icon="BiSad" size={28} /><Title level={4}>{errorMessage?.includes("only compare") ? "Unable to compare these learners" : "Unable to load comparison"}</Title><Text>{errorMessage || "Some learning statistics are not available."}</Text><Button type="default" text="Retry" onClick={refetch} /></section>{canUseDemoData && <DemoDataToggle value={useDemoData} onChange={setUseDemoData} label="Compare data" />}</main>
    </PageLayout>
  );

  const current = data.currentUser;
  const other = data.comparisonUser;
  const specialties = data.specialties || [];
  return (
    <PageLayout backIcon label={<Title level={3}>Compare</Title>}>
      <main className={styles.page}>
        <header className={styles.heading}>
          <div><span className={styles.eyebrow}>Head-to-head</span><Title level={3}>Compare learning progress</Title><Text>Explore strengths across lifetime learning activity.</Text></div>
          {isFetching && <Spinner size="small" />}
        </header>

        <section className={styles.hero} aria-label="Learners being compared">
          <ProfileIdentity person={current} label="You" onOpen={() => navigate(getUserProfilePath(current.userCode))} />
          <div className={styles.heroVs}>vs</div>
          <ProfileIdentity person={other} label="Following" onOpen={() => navigate(getUserProfilePath(other.userCode))} />
        </section>

        <section className={styles.section} aria-labelledby="overall-heading">
          <div className={styles.sectionHeading}><div><Title level={4} id="overall-heading">Overall performance</Title><Text>Lifetime quiz and study activity</Text></div>{data.restricted && <Text className={styles.privacyNotice}>Some statistics are private.</Text>}</div>
          <div className={styles.metricsGrid}>
            <OverallMetric icon="MdOutlineQuiz" label="Questions answered" current={current.stats?.questionsAnswered} other={other.stats?.questionsAnswered} otherPrivate={data.visibility?.comparisonUser?.questions === false} />
            <OverallMetric icon="AiOutlineStar" label="Quiz accuracy" current={current.stats?.accuracy} other={other.stats?.accuracy} suffix="%" otherPrivate={data.visibility?.comparisonUser?.accuracy === false} />
            <OverallMetric icon="HiFire" label="Study streak" current={current.stats?.streak} other={other.stats?.streak} suffix=" days" otherPrivate={data.visibility?.comparisonUser?.streak === false} />
          </div>
        </section>

        <section className={styles.section} aria-labelledby="specialties-heading">
          <div className={styles.sectionHeading}><div><Title level={4} id="specialties-heading">Specialty performance</Title><Text>Accuracy from answered quiz questions, grouped by specialty.</Text></div></div>
          {specialties.length ? <div className={styles.specialties}>{specialties.map((item) => <SpecialtyRow key={item.name} item={item} otherName={other.name || "They"} isOtherPrivate={data.visibility?.comparisonUser?.accuracy === false} />)}</div> : <div className={styles.emptySpecialties}><Icon icon="AiOutlineBook" size={22} /><strong>No specialty comparison available</strong><Text>One or both learners do not have enough specialty-level quiz activity yet.</Text></div>}
        </section>
        {canUseDemoData && <DemoDataToggle value={useDemoData} onChange={setUseDemoData} label="Compare data" />}
      </main>
    </PageLayout>
  );
};

export default Compare;
